import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { IntegrationsState } from '@app/modules/taxilla-library/lib/store/interface/integration-state.interface';
import { TransformationsState } from '@app/modules/taxilla-library/lib/store/states';
import { RootScope } from '@encomply-core/services/rootscope/rootscope.service';
import { translate } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { combineLatest, filter, of, Subject, takeUntil } from 'rxjs';
import { AppIntegrationRecord } from '../../models/integrations/appintegrationrecord.class';
import { AppNewIntegrationRecordPayload } from '../../models/integrations/appnewintegrationpayload.class';
import { AssetService } from '../../models/assetservice.class';
import { Chain } from '../../models/chain.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import {
    DeleteConnector,
    GetConnectors,
    GetDynamicParamsKeys,
    GetIntegrationsConfig,
    SubmitConnector,
} from '../../store/actions/integrations.actions';
import { GetActiveTransformationsByType, GetIntegrationsChains } from '../../store/actions/transformations.actions';
import { GetAllApps } from '../../store/actions/manage-apps.actions';
import { getAllApps$ } from '../../store/selectors/manage-apps.selector';
import { getAllTransformationsServicesMap$ } from '../../store/selectors/transformations.selector';
import { getAssociatedOrganizationsIdVsNameMap$, getOrgIdVsNameMap$ } from '../../store/selectors/locations.selector';
import {
    getConnector$,
    getConnectorId$,
    getConnectors$,
    getIntegrationsSupportedTypes$,
} from '../../store/selectors/integrations.selector';
import {
    getCurrentOrganizationId$,
    isProviderOrganization$,
    isProviderOrganizationAndAdmin$,
} from '../../store/selectors/session.selector';
import { GetOrganizationNamesByIds } from '../../store/actions/locations.actions';
import { IntegrationConfigField, IntegrationModal } from '../../interface/integrationmodal.interface';
import { IntegrationsConfigService } from '../../services/integrations-config/integrations-config.service';
import { IntegrationsUtilsService } from '../../services/integrations-utils/integrations-utils.service';
import { UtilsService } from '../../services/utils/utils.service';

@Component({
    selector: 'app-connectors',
    templateUrl: './connectors.component.html',
    styleUrls: ['./connectors.component.scss'],
    standalone: false,
})
export class ConnectorsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('drawer') drawer: MatDrawer;

    public searchText: string;
    public title: string;
    public config: IntegrationModal = CommonUtilsService.cloneObject(this._config.connectors);
    public record: AppIntegrationRecord = new AppIntegrationRecord({} as any, this.config);
    public sources: { id: string; displayName: string; value?: string | boolean; pushValue?: string }[] = [];
    public bridge: any;
    public connectors: IntegrationsState['connectors']['111'];
    public isProviderAdmin: boolean;
    public isProviderOrganization: boolean;
    private unsubscribe = new Subject<void>();
    private allSubscribedApps: AssetService[] = [];
    private allAppsChains: TransformationsState['organizationsMap']['111']['services'];
    private orgIdVsNames: { [property: string]: string };
    public currentOrganizationId: string;
    public onNewUI: boolean;

    constructor(
        private _config: IntegrationsConfigService,
        public R: RootScope,
        private store$: Store,
        private _libUtils: UtilsService,
        private router: Router,
        private _commonUtils: CommonUtilsService,
        public _integrationUtils: IntegrationsUtilsService
    ) {}

    public searchSystems = () => {};

    public clearSearch = () => {
        this.searchText = undefined;
        this.searchSystems();
    };

    public closeDrawer = () => {
        if (!this.drawer?.opened) {
            return;
        }
        this.drawer.toggle();
    };

    public addConnector = connector => {
        this.clearSearch();
        this.title = connector.connectorDisplayName ? 'Edit Connector: ' + connector.connectorDisplayName : 'Add Connector';
        this.record = new AppIntegrationRecord(connector, this.config);
        if (this.drawer?.opened) {
            return;
        }
        this.drawer?.toggle();
    };

    prepareAuthorizationSystemOptionsActions = (
        systems: {
            displayName: string;
            id: string;
        }[]
    ) => {
        const options = [];
        systems.forEach(system => {
            const option = {};
            option['id'] = system.id;
            option['displayName'] = system.displayName;
            option['value'] = system.id;
            option['actionsRequired'] = this.isProviderAdmin;
            option['actions'] = [{ name: 'Edit', icon: 'edit' }];
            options.push(option);
        });
        return options;
    };

    private updateAuthorizationSystemFields = (fields: IntegrationConfigField[]) => {
        fields?.forEach(field => {
            if (field.id === 'sourceSystem') {
                field.createOptionRequired = this.isProviderAdmin;
            }
        });
    };

    private addAuthorizations = () => {
        const authSystem = this.config?.subSections.find(s => s.id === 'sourceSystem')?.fields;
        const recordAuthSystem = this.record?.recordData?.subSections.find(s => s.id === 'sourceSystem')?.fields;
        this.updateAuthorizationSystemFields(authSystem);
        recordAuthSystem && this.updateAuthorizationSystemFields(recordAuthSystem);
        this.record?.recordData?.fields && this.updateAuthorizationSystemFields(this.record.recordData.fields);
    };

    public appChanged = (restApiName: string) => {
        const app = this.allSubscribedApps?.find(service => service.restApiName === restApiName);
        if (!app || !app.restApiName) {
            return;
        }
        this.store$.dispatch(
            GetActiveTransformationsByType({
                noAlerts: true,
                serviceId: app?.serviceId,
                restApiName: restApiName,
                transformationType: 'INBOUND',
                forceFetch: false,
            })
        );
        this.store$.dispatch(
            GetIntegrationsChains({
                serviceId: app?.serviceId,
                restApiName: restApiName,
                assetId: app?.assetMetaUId,
                assetName: app?.serviceName,
            })
        );
        this.checkChains();
    };

    checkChains = () => {
        const notFoundOrgs = [];
        this.record?.recordData?.subSections
            ?.find(section => section.id === 'connectorApis')
            ?.records?.forEach(record => {
                const subsection = record?.childSubSections?.find(s => s.displayName === 'Target');
                const restApiName = subsection?.fields.find(f => f.id === 'assetResourceName')?.value;
                const app = this.allSubscribedApps?.find(service => service.restApiName === restApiName);
                const transformations = CommonUtilsService.cloneObject(this.allAppsChains?.[app?.restApiName]?.transformations);
                const chains: Chain[] = transformations?.integrationsChains?.inbound || [];
                const activeTransformations = transformations?.activeTransformations?.inbound || [];
                const chainsSorted = chains
                    .sort((a, b) => {
                        if (activeTransformations?.includes(a.chainName) && activeTransformations?.includes(b.chainName)) {
                            return activeTransformations?.indexOf(a.chainName) - activeTransformations?.indexOf(b.chainName);
                        } else if (activeTransformations?.includes(a.chainName)) {
                            return -1;
                        } else if (activeTransformations?.includes(b.chainName)) {
                            return 1;
                        }
                        return 0;
                    })
                    .filter(chain => activeTransformations?.length === 0 || activeTransformations?.includes(chain.chainName));
                if (!chainsSorted || chainsSorted.length === 0) {
                    return;
                }
                const orgIdVsNames = this.orgIdVsNames;
                const providerOrgId = this._commonUtils.getFromStorage('currentOrganization')?.providerOrganizationId;
                const chainslist = [];
                for (const key in orgIdVsNames) {
                    const rawOrgName = orgIdVsNames[key];
                    chainslist.push({
                        orgName: providerOrgId && key === providerOrgId ? 'Default' : rawOrgName,
                        repositoryId: key,
                        chains: [],
                    });
                }
                chainsSorted?.forEach(chain => {
                    let found = false;
                    chainslist.forEach(org => {
                        if (org.repositoryId === chain.repositoryId) {
                            org.chains.push(chain);
                            found = true;
                        }
                    });
                    if (!found) {
                        notFoundOrgs.push(chain.repositoryId);
                    }
                });
                const chainField = subsection?.fields.find(f => f.type === 'CONNECTORS_CHAIN_DROPDOWN');
                if (chainField) {
                    chainField.options = chainslist;
                }
            });
        notFoundOrgs?.length > 0 && this.store$.dispatch(GetOrganizationNamesByIds({ orgIds: notFoundOrgs, noAlerts: true }));
    };

    closeEdit = () => {
        this.store$.dispatch(GetConnectors());
        const url = '/organizations/' + this.currentOrganizationId + (!this.onNewUI ? '/settings' : '') + '/connectors';
        this.router.navigate([url]);
    };

    private validateRecord = (record?: AppIntegrationRecord, callback?: (...args: any) => void): boolean => {
        const result = this._integrationUtils.isFormValid((record as any) || this.record);
        if (!result) {
            this._libUtils.alertError(translate('Fix issues to proceed'));
        }
        callback && callback();
        return result;
    };

    public submitConfiguration = (callback?: (...args: any[]) => void) => {
        if (this.validateRecord()) {
            const record = CommonUtilsService.cloneObject(this.record);
            const config = CommonUtilsService.cloneObject(this.config);
            const data = new AppNewIntegrationRecordPayload(record, config, undefined, undefined, undefined);
            this.saveConnectorConfiguration(data, callback);
        }
    };

    public saveConnectorConfiguration = (payload, callback?: (...args: any[]) => void) => {
        this.store$.dispatch(
            SubmitConnector({
                connector: payload,
                update: this.record?.originalData?.resourceName?.length > 0,
            })
        );
        callback && callback();
    };

    public deleteConnector = (connector: IntegrationsState['connectors']['111'][0]) => {
        this.store$.dispatch(DeleteConnector({ resourceName: connector?.resourceName }));
    };

    private handleConnectorRouteRedirect(): void {
        this.onNewUI = this._commonUtils.getCookie('new-ui') === 'true';
        const url = this.router.url;

        if (this.onNewUI && url.includes('/settings/connectors')) {
            this.router.navigate([`/organizations/${this.currentOrganizationId}/connectors`]);
        }

        if (!this.onNewUI && url.includes('/connectors') && !url.includes('/settings')) {
            this.router.navigate([`/organizations/${this.currentOrganizationId}/settings/connectors`]);
        }
    }

    initialSubScriptions = () => {
        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(orgId => {
                this.currentOrganizationId = orgId;
            });
        this.store$
            .select(isProviderOrganizationAndAdmin$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(event => {
                this.isProviderAdmin = event;
                this.addAuthorizations();
            });
        this.store$
            .select(isProviderOrganization$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(event => {
                this.isProviderOrganization = event;
            });
        this.store$
            .select(getAllTransformationsServicesMap$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(async data => {
                this.allAppsChains = CommonUtilsService.cloneObject(data);
                this.checkChains();
            });
        this.store$
            .select(getAssociatedOrganizationsIdVsNameMap$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this.orgIdVsNames = data;
            });
        this.store$
            .select(getIntegrationsSupportedTypes$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this._integrationUtils.updateSupportedTypes(this.config, data);
                this.record?.recordData && this._integrationUtils.updateSupportedTypes(this.record.recordData, data);
            });
        this.store$
            .select(getAllApps$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this.allSubscribedApps = CommonUtilsService.cloneObject(data)?.filter(app => app.assetType === 'ASSET');
                const configAppSelection = this.config?.subSections
                    ?.find(s => s.id === 'connectorApis')
                    ?.childSubSections?.find(s => s.displayName === 'Target')
                    ?.fields?.find(configField => configField.id === 'assetResourceName');
                if (configAppSelection) {
                    configAppSelection.options = this.allSubscribedApps?.map(app => ({
                        id: app.restApiName as string,
                        displayName: app.displayName,
                    }));
                }
                this.record?.recordData?.subSections
                    ?.find(s => s.id === 'connectorApis')
                    ?.records?.forEach(record => {
                        const recordappSelection = record?.childSubSections
                            ?.find(s => s.displayName === 'Target')
                            ?.fields?.find(configField => configField.id === 'assetResourceName');
                        if (recordappSelection) {
                            this.appChanged(recordappSelection.value);
                            recordappSelection.options = this.allSubscribedApps?.map(app => ({
                                id: app.restApiName as string,
                                displayName: app.displayName,
                            }));
                        }
                    });
            });
        this.store$
            .select(getConnectors$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(data => {
                this.connectors = data;
            });
        this.store$
            .select(getIntegrationsSupportedTypes$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(supportedTypes => {
                if (supportedTypes) {
                    this._integrationUtils.updateSupportedTypes(this.config, supportedTypes);
                    this.record?.recordData && this._integrationUtils.updateSupportedTypes(this.record.recordData, supportedTypes);
                }
            });
        const connectorId$ = this.store$.select(getConnectorId$) || of('');
        const connector$ = this.store$.select(getConnector$) || of(undefined);

        combineLatest([connectorId$, connector$])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([connectorName, connector]) => {
                if (connectorName?.length > 0 && connector) {
                    this.addConnector(connector);
                } else if (connectorName === 'new') {
                    this.addConnector({});
                } else if (!connectorName) {
                    this.closeDrawer();
                }
            });
        this.store$
            .select(getOrgIdVsNameMap$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this.orgIdVsNames = data;
                this.checkChains();
            });
    };

    ngOnInit() {}

    ngAfterViewInit() {
        this.initialSubScriptions();
        this.store$.dispatch(GetAllApps({ isPackagePage: false, forceFetch: true, allApps: true }));
        this.handleConnectorRouteRedirect();
        this.store$.dispatch(GetIntegrationsConfig({}));
        this.store$.dispatch(GetConnectors());
        this.store$.dispatch(GetDynamicParamsKeys());
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
